import { List, Map } from "immutable";
import { createSelector } from "reselect";

export const getJobs = (state) => state.jobs.get("jobs", List());
export const getJobMap = (state) => state.jobs.get("memoryJobMap", Map());

export const getJobCount = createSelector(getJobs, (jobs) => jobs.size);

/**
 * Get the number of jobs running with `jobName`
 * @param {*} state
 * @param {*} jobName
 */
export const getSingleJobCount = (state, jobName) => getJobs(state).filter((item) => item.get("name") === jobName).size;

/**
 * Returns a job from the jobID.
 * @param {state} state
 * @param {string} jobID - id of the job
 */
export const getJobObject = (state, jobID, notSetValue = Map()) => getJobMap(state).get(jobID, notSetValue);

/**
 * Returns a job given the job name.
 * @param {state} state
 * @param {string} jobName - id of the job
 */
export const getJobObjectByName = (state, jobName) =>
	getJobs(state)
		.filter((item) => item.get("name") === jobName)
		.get(0);

/**
 * Returns the current progress of a job, returns 0 if not found.
 * @param {state} state
 * @param {string} jobID - id of the job
 */
export const getJobProgress = (state, jobID) => {
	const job = getJobObject(state, jobID, null);

	if (!job) {
		return 0;
	}

	let progressCount = job.get("progress_count", 0);
	let progressTotal = job.get("progress_total", 1);

	progressCount = progressCount === null ? 0 : progressCount;
	progressTotal = progressTotal === null ? 1 : progressTotal;

	return progressCount / progressTotal;
};

/**
 * Returns the current state of a job, returns false if not found.
 * @param {state} state
 * @param {string} jobID - id of the job
 */
export const getIsJobRunningID = (state, jobID) => {
	const job = getJobObject(state, jobID, null);

	if (!job) {
		return false;
	}

	const started = job.get("started", false);
	const finished = job.get("finished", false);

	return started === true && finished === false;
};

/**
 * Returns whether or not a job has finished.
 * @param {state} state
 * @param {string} jobID - id of the job
 */
export const getIsJobCompleted = (state, jobID) => {
	const job = getJobObject(state, jobID, null);

	if (!job) {
		return false;
	}

	return job.get("finished", false);
};

/**
 * Returns the download url of a job, if one exits.
 * @param {state} state
 * @param {string} jobID - id of the job
 */
export const getJobFileDownload = (state, jobID) => {
	const job = getJobObject(state, jobID, null);

	if (!job) {
		return null;
	}

	return job.get("redirectURL", null);
};

/**
 * Returns the first error message recorded against a job, or null if it recorded none.
 *
 * Serialized from the job's `errors` field under the `todos` key. A non-empty list is the only
 * reliable failure signal on the websocket payload: `redirectURL` is populated for just the first
 * job in each message, so an absent URL does not mean the job failed.
 *
 * @param {state} state
 * @param {string} jobID - id of the job
 */
export const getJobErrorMessage = (state, jobID) => {
	const job = getJobObject(state, jobID, null);

	if (!job) {
		return null;
	}

	// `todos` is serialized as null, not omitted, when the job recorded no errors
	return (job.get("todos") || List()).first()?.get("message") ?? null;
};

/**
 * Returns the download url of a job, if one exists.
 * @param {state} state
 * @param {string} jobName - id of the job
 */
export const getJobFileDownloadByJobName = (state, jobName) => {
	const job = getJobObjectByName(state, jobName);

	if (!job) {
		return null;
	}

	return job.get("redirectURL", null);
};

export const getIsJobRunning = (state, jobName) => {
	const job = getJobs(state).find((item) => item.get("name") === jobName, null, Map());
	return job.get("finished") === false && job.get("started") === true;
};

export const getJobStatus = createSelector([getJobCount], (count) => {
	if (count === 0) {
		return "idle";
	} else if (count > 0) {
		return "running";
	}

	return "idle";
});

/**
 * Return the state of websocket connection, default to false
 * @param state
 * @returns {*}
 */
export function getWebsocketStatus(state) {
	return state.jobs.get("failedWebsocketStatus", false);
}
